import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "./config/API";

function ReferralList() {
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));

  // -----------------------------
  // Left Menu
  // -----------------------------
  const [selectedMenu, setSelectedMenu] = useState("dashboard");

  // -----------------------------
  // Referral Data
  // -----------------------------
  const [referrals, setReferrals] = useState([]);

  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");

  const [popup, setPopup] = useState("");

  // -----------------------------
  // Referral Link
  // -----------------------------
  const referralLink = `${window.location.origin}/pgwelcomepage?ref=${user?.referralId}`;

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);

    showPopup("Referral Link Copied Successfully");
  };

  // -----------------------------
  // Dashboard Summary
  // -----------------------------
  const filteredData = useMemo(() => {
    return referrals.filter((item) => {
      const keyword = search.toLowerCase();

      return (
        item.pgName?.toLowerCase().includes(keyword) ||
        item.userName?.toLowerCase().includes(keyword) ||
        item.place?.toLowerCase().includes(keyword)
      );
    });
  }, [search, referrals]);

  const totalBalance = useMemo(() => {
    return filteredData.reduce(
      (sum, item) => sum + Number(item.referralAmount || 0),
      0,
    );
  }, [filteredData]);

  const totalReferrals = filteredData.length;

  const totalPending = filteredData.filter(
    (x) => x.paymentStatus === "PENDING",
  ).length;

  const totalCompleted = filteredData.filter(
    (x) => x.paymentStatus === "COMPLETED",
  ).length;

  // -----------------------------
  // Initial Load
  // -----------------------------
  useEffect(() => {
    fetchReferrals();
  }, []);

  // -----------------------------
  // Popup
  // -----------------------------
  const showPopup = (msg) => {
    setPopup(msg);

    setTimeout(() => {
      setPopup("");
    }, 2500);
  }; // -----------------------------
  // Fetch Referral List
  // -----------------------------
  const fetchReferrals = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `${API.V2}/pgOwner/getReferralsByUserId?referredBy=${user?.userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.jwtToken}`,
          },
        },
      );

      const res = await response.json();

      if (response.ok && res.statusCode === 200) {
        setReferrals(res.data || []);
      } else if (response.status === 401) {
        localStorage.clear();
        navigate("/");
      } else {
        showPopup(res.message || "No Referral Records Found");
      }
    } catch (error) {
      console.error(error);
      showPopup("Server Down");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Withdraw Amount
  // -----------------------------
  const handleWithdraw = () => {
    showPopup("Withdraw Request Submitted Successfully");
  };

  // -----------------------------
  // Send Referral
  // -----------------------------
  const sendReferral = () => {
    if (navigator.share) {
      navigator.share({
        title: "SVK PG Referral",
        text: "Join using my referral link",
        url: referralLink,
      });
    } else {
      copyReferralLink();
    }
  };

  return (
    <div style={styles.container}>
      {popup && <div style={styles.popup}>{popup}</div>}

      <div style={styles.mainContainer}>
        {/* ================= LEFT MENU ================= */}

        <div style={styles.sidebar}>
          <h2 style={styles.logo}>Referral Panel</h2>

          <button
            style={{
              ...styles.menuButton,
              ...(selectedMenu === "dashboard" ? styles.activeMenu : {}),
            }}
            onClick={() => setSelectedMenu("dashboard")}
          >
            📊 Dashboard
          </button>

          <button
            style={{
              ...styles.menuButton,
              ...(selectedMenu === "send" ? styles.activeMenu : {}),
            }}
            onClick={() => setSelectedMenu("send")}
          >
            📤 Send Referrals
          </button>

          <button
            style={{
              ...styles.menuButton,
              ...(selectedMenu === "withdraw" ? styles.activeMenu : {}),
            }}
            onClick={() => setSelectedMenu("withdraw")}
          >
            💰 Withdraw Amount
          </button>

          {/* <button
            style={{
              ...styles.menuButton,
              ...(selectedMenu === "link" ? styles.activeMenu : {}),
            }}
            onClick={() => setSelectedMenu("link")}
          >
            🔗 Create Referral Link
          </button> */}
        </div>

        {/* ================= RIGHT CONTENT ================= */}

        <div style={styles.content}>
          {" "}
          {/* ================= DASHBOARD ================= */}
          {selectedMenu === "dashboard" && (
            <>
              <div style={styles.header}>
                <div>
                  <h2 style={styles.title}>Referral Dashboard</h2>

                  <p style={styles.subTitle}>
                    View referral earnings and referral history
                  </p>
                </div>
              </div>

              {/* Summary Cards */}

              <div style={styles.cardGrid}>
                <div style={styles.summaryCard}>
                  <div style={styles.cardTitle}>Total Referrals</div>

                  <div style={styles.cardValue}>{totalReferrals}</div>
                </div>

                <div style={styles.summaryCard}>
                  <div style={styles.cardTitle}>Completed</div>

                  <div
                    style={{
                      ...styles.cardValue,
                      color: "#16a34a",
                    }}
                  >
                    {totalCompleted}
                  </div>
                </div>

                <div style={styles.summaryCard}>
                  <div style={styles.cardTitle}>Pending</div>

                  <div
                    style={{
                      ...styles.cardValue,
                      color: "#dc2626",
                    }}
                  >
                    {totalPending}
                  </div>
                </div>

                <div style={styles.summaryCard}>
                  <div style={styles.cardTitle}>Total Balance</div>

                  <div
                    style={{
                      ...styles.cardValue,
                      color: "#2563eb",
                    }}
                  >
                    ₹ {totalBalance.toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Search */}

              <div style={styles.searchContainer}>
                <input
                  type="text"
                  placeholder="Search by User Name, PG Name or Location..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={styles.searchInput}
                />
              </div>

              {/* Referral Table */}

              {loading ? (
                <div style={styles.loading}>Loading Referral Details...</div>
              ) : filteredData.length === 0 ? (
                <div style={styles.noData}>No Referral Records Found</div>
              ) : (
                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>S.No</th>
                        <th style={styles.th}>User Name</th>
                        <th style={styles.th}>PG Name</th>
                        <th style={styles.th}>Location</th>
                        <th style={styles.th}>Email</th>
                        <th style={styles.th}>Referral Code</th>
                        <th style={styles.th}>Amount</th>
                        <th style={styles.th}>Created Date</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredData.map((item, index) => (
                        <tr key={index}>
                          <td style={styles.td}>{index + 1}</td>

                          <td style={styles.td}>{item.userName}</td>

                          <td style={styles.td}>{item.pgName}</td>

                          <td style={styles.td}>{item.place}</td>

                          <td style={styles.td}>{item.referralEmailId}</td>

                          <td style={styles.td}>
                            <span style={styles.code}>{item.referralId}</span>
                          </td>

                          <td style={styles.amount}>
                            ₹{" "}
                            {Number(item.referralAmount || 0).toLocaleString()}
                          </td>

                          <td style={styles.td}>
                            {new Date(item.createdOn).toLocaleDateString(
                              "en-IN",
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          {/* ================= SEND REFERRALS ================= */}
          {selectedMenu === "send" && (
            <div style={styles.pageCard}>
              <h2 style={styles.pageTitle}>📤 Send Referral</h2>

              <p style={styles.pageSubTitle}>
                Share your referral link with friends and earn rewards whenever
                they register successfully.
              </p>

              <div style={styles.referralBox}>
                <input
                  type="text"
                  readOnly
                  value={referralLink}
                  style={styles.referralInput}
                />

                <button style={styles.copyButton} onClick={copyReferralLink}>
                  📋 Copy Referral
                </button>

                <button style={styles.primaryButton} onClick={sendReferral}>
                  Share Referral
                </button>
              </div>
            </div>
          )}
          {/* ================= WITHDRAW AMOUNT ================= */}
          {selectedMenu === "withdraw" && (
            <div style={styles.pageCard}>
              <h2 style={styles.pageTitle}>💰 Withdraw Amount</h2>

              <div style={styles.withdrawCard}>
                <h3>Total Available Balance</h3>

                <h1
                  style={{
                    color: "#16a34a",
                    marginTop: "15px",
                  }}
                >
                  ₹ {totalBalance.toLocaleString()}
                </h1>

                <button
                  style={{
                    ...styles.primaryButton,
                    marginTop: "30px",
                  }}
                  onClick={handleWithdraw}
                >
                  Withdraw Amount
                </button>
              </div>
            </div>
          )}
          {/* ================= CREATE REFERRAL LINK ================= */}
          {/* {selectedMenu === "link" && (
            <div style={styles.pageCard}>
              <h2 style={styles.pageTitle}>🔗 Create Referral Link</h2>

              <p style={styles.pageSubTitle}>
                Copy your personal referral link and share it.
              </p>

              <div style={styles.referralBox}>
                <input
                  readOnly
                  value={referralLink}
                  style={styles.referralInput}
                />

                <button style={styles.primaryButton} onClick={copyReferralLink}>
                  Copy Link
                </button>
              </div>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
}
export default ReferralList;
const styles = {
  mainContainer: {
    display: "flex",
    minHeight: "100vh",
    background: "#f4f7fb",
    fontFamily: "Arial,sans-serif",
  },

  /* ================= LEFT MENU ================= */

  sidebar: {
    width: "260px",
    background: "#1e3a8a",
    color: "#fff",
    padding: "25px 0",
    boxShadow: "3px 0 15px rgba(0,0,0,.15)",
  },

  logo: {
    textAlign: "center",
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "30px",
  },

  menuButton: {
    width: "100%",
    border: "none",
    background: "transparent",
    color: "#fff",
    textAlign: "left",
    padding: "16px 24px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
    transition: ".3s",
  },

  activeMenu: {
    background: "#2563eb",
    borderLeft: "5px solid #fff",
  },

  /* ================= RIGHT CONTENT ================= */

  content: {
    flex: 1,
    padding: "30px",
  },

  header: {
    marginBottom: "25px",
  },

  title: {
    margin: 0,
    fontSize: "30px",
    color: "#1e3a8a",
    fontWeight: "700",
  },

  subTitle: {
    marginTop: "8px",
    color: "#64748b",
    fontSize: "15px",
  },

  /* ================= DASHBOARD CARDS ================= */

  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
    gap: "20px",
    marginBottom: "30px",
  },

  summaryCard: {
    background: "#fff",
    borderRadius: "14px",
    padding: "22px",
    boxShadow: "0 6px 18px rgba(0,0,0,.10)",
  },

  cardTitle: {
    color: "#64748b",
    fontSize: "15px",
  },

  cardValue: {
    marginTop: "15px",
    fontSize: "34px",
    fontWeight: "700",
    color: "#1e293b",
  },

  /* ================= SEARCH ================= */

  searchContainer: {
    marginBottom: "20px",
  },

  searchInput: {
    width: "100%",
    padding: "14px",
    borderRadius: "10px",
    border: "1px solid #d1d5db",
    fontSize: "15px",
    outline: "none",
    boxSizing: "border-box",
  },

  /* ================= TABLE ================= */

  tableWrapper: {
    overflowX: "auto",
    borderRadius: "12px",
    background: "#fff",
    boxShadow: "0 5px 15px rgba(0,0,0,.08)",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1050px",
  },

  th: {
    background: "#2563eb",
    color: "#fff",
    padding: "15px",
    textAlign: "left",
    fontWeight: "700",
  },

  td: {
    padding: "14px",
    borderBottom: "1px solid #e5e7eb",
  },

  amount: {
    padding: "14px",
    color: "#16a34a",
    fontWeight: "700",
    borderBottom: "1px solid #e5e7eb",
  },

  code: {
    background: "#dbeafe",
    color: "#1d4ed8",
    padding: "5px 10px",
    borderRadius: "20px",
    fontWeight: "700",
  },

  /* ================= PAGE CARD ================= */

  pageCard: {
    background: "#fff",
    borderRadius: "15px",
    padding: "35px",
    boxShadow: "0 6px 20px rgba(0,0,0,.12)",
  },

  pageTitle: {
    margin: 0,
    color: "#1e3a8a",
    fontSize: "28px",
    fontWeight: "700",
  },

  pageSubTitle: {
    color: "#64748b",
    marginTop: "10px",
    marginBottom: "30px",
  },

  /* ================= REFERRAL LINK ================= */

  referralBox: {
    display: "flex",
    gap: "15px",
    flexWrap: "wrap",
  },

  referralInput: {
    flex: 1,
    padding: "14px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    fontSize: "15px",
  },

  primaryButton: {
    padding: "14px 24px",
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "700",
  },

  /* ================= WITHDRAW ================= */

  withdrawCard: {
    background: "#f8fafc",
    borderRadius: "15px",
    padding: "40px",
    textAlign: "center",
    border: "1px solid #dbeafe",
  },

  /* ================= STATUS ================= */

  loading: {
    padding: "50px",
    textAlign: "center",
    fontSize: "20px",
    color: "#2563eb",
    fontWeight: "700",
  },

  noData: {
    padding: "50px",
    textAlign: "center",
    fontSize: "20px",
    color: "#dc2626",
    fontWeight: "700",
  },

  popup: {
    position: "fixed",
    top: "20px",
    right: "20px",
    background: "#111827",
    color: "#fff",
    padding: "14px 22px",
    borderRadius: "8px",
    zIndex: 9999,
    fontWeight: "700",
  },
  copyButton: {
    padding: "14px 24px",
    background: "#16a34a",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "700",
    whiteSpace: "nowrap",
  },

  shareButton: {
    padding: "14px 24px",
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "700",
    whiteSpace: "nowrap",
  },
};
